import type {DetectionConflictInterface} from './detectionConflict'
import type {ProgramClassificationInterface} from './programClassification'
import type {TriggerGroupInterface} from './triggerGroup'

export interface AnomalyProgramInterface {
  id: string
  name: string
}

export type BatchChangeoverPeriod = 'hour' | 'day' | 'week' | 'month' | 'year'

/** Extract the classification name from either a string or nested object returned by the API. */
export const getClassificationName = (
  classification: string | ProgramClassificationInterface | null | undefined,
): string => {
  if (!classification) return ''
  if (typeof classification === 'string') return classification
  return classification.name || ''
}

export interface ProgramInterface {
  id: string
  name: string
  description: string
  classification: string | ProgramClassificationInterface
  machine: string
  machine_sub_system: {
    id: string
    name: string
    t_batch_determination_checkpoint: string
  }
  target_machine_sub_system: string | null
  /**
   * What detects this program: its condition groups, combined by
   * `triggers_truth_eval` (AB#6181). There is no flat `triggers` list and no
   * `program_detectors` any more -- a flat set was the same concept as a group
   * minus a name and an operator, so the storage collapsed onto one.
   */
  triggers_truth_eval: 'all' | 'any'
  trigger_groups: TriggerGroupInterface[]
  step_signal?: string
  trace_quantity_signal_rollup: string | null
  trace_termination_trigger: string | null
  trace_role: string
  trace_trigger: string
  trace_start_enabled?: boolean
  trace_start_trigger?: 'on_start' | 'on_end'
  trace_end_enabled?: boolean
  trace_end_trigger?: 'on_start' | 'on_end'
  /**
   * False (default until someone touches trace config) means this program's
   * effective trace behavior comes from its classification's default, not
   * from the trace_* fields above -- see ProgramClassificationInterface's
   * default_trace_* fields (AB#5133).
   */
  trace_config_is_manual?: boolean
  trace_update_quantity_based_on_signal_rollup: boolean
  trace_quantity_allowed_deviation: number
  n_batches: number
  detection_conflict: DetectionConflictInterface | null
  anomaly_detection_enabled: boolean
  is_enabled: boolean
  value_based_anomaly_detection_enabled: boolean
  vb_ad_enabled_signals: string[]
  batch_changeover_signals: string[]
  batch_changeover_period: BatchChangeoverPeriod | null
  batch_changeover_timezone: string | null
  production_rate?: number
}

export interface FileUploadResponse {
  message: string
  data: ProgramInterface[]
  warnings?: string[]
}

export interface ProgramUploadErrorResponse {
  status: number
  data: {
    error: string
  }
}

export interface MigrationResponse {
  machine_sub_system_id: string
  machine_sub_system_name: string
  old_machine: {
    id: string
    name: string
  }
  new_machine: {
    id: string
    name: string
  }
  signals_updated: number
  special_signals_updated: number
}
